import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { teachersApi, lessonsApi, type TeacherProfile, type Slot } from "../lib/lessons";

function fmt(ts: number): string {
  return new Date(ts).toLocaleString("pl-PL", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function BookTeacherPage(): JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState<TeacherProfile[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    teachersApi.list().then((r) => setTeachers(r.teachers)).catch((e) => setError(String(e)));
  }, []);

  useEffect(() => {
    if (!selected) {
      setSlots([]);
      return;
    }
    teachersApi
      .availability(selected, Date.now(), Date.now() + 30 * 24 * 60 * 60 * 1000)
      .then((r) => setSlots(r.slots))
      .catch((e) => setError(String(e)));
  }, [selected]);

  async function book(slot: Slot, teacher: TeacherProfile): Promise<void> {
    if (!confirm(`Zarezerwować ${fmt(slot.starts_at)} u ${teacher.name ?? "nauczyciela"}?`)) return;
    setBusy(true);
    try {
      const { lesson } = await lessonsApi.create({
        title: `Lekcja z ${teacher.name ?? "nauczycielem"}`,
        starts_at: slot.starts_at,
        ends_at: slot.ends_at,
      });
      await lessonsApi.join(lesson.id);
      navigate(`/lessons/${lesson.id}`);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!user) return <Link to="/" className="text-white p-6 block">Zaloguj się</Link>;

  return (
    <div className="h-full bg-ink-900 text-white">
      <header className="px-6 py-4 border-b border-ink-700">
        <Link to="/" className="text-xs text-ink-400 hover:text-white">← Wróć</Link>
        <h1 className="text-lg font-semibold mt-1">Znajdź nauczyciela</h1>
      </header>

      <main className="max-w-3xl mx-auto p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <aside className="md:col-span-1">
          <h2 className="text-xs uppercase tracking-wider text-ink-400 mb-2">Nauczyciele</h2>
          <ul className="space-y-1">
            {teachers.map((t) => (
              <li key={t.user_id}>
                <button
                  onClick={() => setSelected(t.user_id)}
                  className={`w-full text-left px-3 py-2 rounded-lg border ${
                    selected === t.user_id
                      ? "bg-lime-500/10 border-lime-500 text-white"
                      : "bg-ink-800 border-ink-700 hover:bg-ink-700"
                  }`}
                >
                  <div className="font-medium">{t.name ?? "Anonim"}</div>
                  {t.subjects.length > 0 && (
                    <div className="text-xs text-ink-400">{t.subjects.join(", ")}</div>
                  )}
                </button>
              </li>
            ))}
            {teachers.length === 0 && (
              <li className="text-ink-500 text-sm">Brak nauczycieli. Przejdź do "Kalendarz" by zostać nauczycielem.</li>
            )}
          </ul>
        </aside>

        <section className="md:col-span-2">
          <h2 className="text-xs uppercase tracking-wider text-ink-400 mb-2">Wolne terminy</h2>
          {error && <div className="text-red-300 text-sm mb-2">{error}</div>}
          {!selected && <p className="text-ink-500 text-sm">Wybierz nauczyciela z listy</p>}
          {selected && slots.length === 0 && (
            <p className="text-ink-500 text-sm">Brak wolnych terminów w najbliższych 30 dniach.</p>
          )}
          <ul className="space-y-2">
            {slots.map((s) => {
              const teacher = teachers.find((t) => t.user_id === selected);
              return (
                <li
                  key={s.id}
                  className="bg-ink-800 border border-ink-700 rounded-lg px-4 py-2 text-sm flex items-center justify-between"
                >
                  <span>{fmt(s.starts_at)} – {new Date(s.ends_at).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}</span>
                  <button
                    disabled={busy || !teacher}
                    onClick={() => teacher && book(s, teacher)}
                    className="bg-lime-500 hover:bg-lime-400 disabled:bg-ink-700 text-ink-900 rounded px-3 py-1 text-xs font-semibold"
                  >
                    Zarezerwuj
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      </main>
    </div>
  );
}
